import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { Link, RouteComponentProps } from 'react-router';
import { reduxForm, Field, change, getFormSyncErrors } from 'redux-form';
import { BottomButton, ErrorSummary, LoadingOverlayWrapper } from 'components';
import 'react-accessible-accordion/dist/fancy-example.css';
import { IManageProduct } from 'models/product/manageproduct';
import { IProductForm } from 'models/product/product';
import { IStore } from 'redux/IStore';
import { getProductVariants, clearFormData, submitForm } from 'modules/product/manageproduct';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { preventRouterLeave } from 'helpers/hooks';
import { Dispatch } from 'react-redux';
import { attributesDefinitions, createInitialValues, mapErrorKeyToMessage } from 'helpers/products';
import { ProductVariations } from '../ProductVariations';
import { EditMainProduct } from '../EditMainProduct';
import renderIcon from 'helpers/renderIcon';
import { domainValuesMap } from 'redux/modules/domainvalues';
const { connect } = require('react-redux');

interface IParams {
	id: string;
	variantParentId?: string;
}

interface IOwnProps extends RouteComponentProps<IParams, {}> {}

interface IProps
	extends IOwnProps,
		InjectedFormProps<IProductForm>,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		ITranslateProps {}

interface IState {
	step: 1 | 2;
}

@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class EditProductForm extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.state = { step: 1 };

		this.onSubmit = this.onSubmit.bind(this);
		this.goToStep = this.goToStep.bind(this);
	}

	public componentWillUnmount() {
		this.props.clearFormData();
	}

	public componentDidMount() {
		if (this.mode === 'edit') {
			this.props.loadData();
		}
		this.props.getOptionsData();
	}

	public goToStep(step: 1 | 2) {
		if (!this.props.valid) {
			if (this.mode === 'edit' || step === 2) {
				return;
			}
		}
		this.setState({ step });
	}

	public componentDidUpdate(prevProps: IProps) {
		if (prevProps.params.variantParentId !== this.props.params.variantParentId) {
			if (this.mode === 'add') {
				this.props.clearFormData();
			} else {
				this.props.loadData();
			}
		}
	}

	private get mode(): 'add' | 'edit' {
		if (this.props.params.variantParentId) {
			return 'edit';
		}
		return 'add';
	}

	public onSubmit(form: IProductForm) {
		form = JSON.parse(JSON.stringify(form));
		if (this.state.step === 1 && this.mode === 'add') {
			return this.goToStep(2);
		}

		form.shopId = this.props.params.id;
		if (!form.attributes) {
			form.attributes = {};
		}
		form.attributes.CategoryKey = null;

		if (form.thirdDropdownCategoryKey) {
			form.attributes.CategoryKey = form.thirdDropdownCategoryKey;
		} else if (form.secondDropdownCategoryKey) {
			form.attributes.CategoryKey = form.secondDropdownCategoryKey;
		} else if (form.firstDropdownCategoryKey) {
			form.attributes.CategoryKey = form.firstDropdownCategoryKey;
		}

		if (form.images) {
			form.images = form.images.filter((x) => x.id && x.href);
		}

		this.props.submitForm(form);
	}

	public render() {
		const { handleSubmit, t, formErrors, submitFailed, location, valid, pristine, submitting, currentFormValues } =
			this.props;
		const { step } = this.state;
		const { error, message } = this.props.manageProduct;

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error,
			message,
			location,
			mapErrorKeyToMessage,
		};

		return (
			<LoadingOverlayWrapper
				className={'boxedLayout container-fluid topPadding editProduct text-center ' + (step === 2 ? ' wide' : '')}
				active={this.props.manageProduct.isFetching}
				spinner={true}
				text={t('BSPortal_Common_Loading')}
				background="rgba(0, 0, 0, 0.1)"
			>
				<aside>
					<h1 className="text-center d-block d-lg-none">
						{t(
							this.mode === 'add'
								? 'Shop_ManageShop_Products_CreateProduct_Title'
								: 'Shop_ManageShop_Products_EditProduct_Title',
						)}
					</h1>
					<div className="sidemenu">
						<ul>
							<li
								className={step === 1 ? 'active enabled' : valid || this.mode === 'add' ? 'enabled' : ''}
								onClick={() => this.goToStep(1)}
							>
								{t('BSPortal_Common_Info')}
							</li>
							<li className={step === 2 ? 'active enabled' : valid ? 'enabled' : ''} onClick={() => this.goToStep(2)}>
								{t('Shop_ManageShop_Products_Inventory_Title')}
							</li>
						</ul>
					</div>
				</aside>
				<form onSubmit={handleSubmit(this.onSubmit)} className="main">
					<Field name="id" component="input" type="hidden" />
					<h5 className="d-none d-lg-block">{step}/2</h5>
					<div className="text-left">
						<div className="profileError">
							<ErrorSummary {...errorSummaryProps} />
						</div>
						{currentFormValues ? (
							step === 1 ? (
								<EditMainProduct {...this.props} />
							) : (
								<ProductVariations {...this.props} />
							)
						) : null}
					</div>
					<BottomButton type="submit" disabled={pristine || submitting}>
						{t(this.mode === 'add' && step === 1 ? 'BSPortal_Common_Next' : 'BSPortal_Common_Save')}
					</BottomButton>
				</form>
				<Link
					to={`/shop/${this.props.params.id}/edit/products`}
					className="btnIcon closeLayout"
					title={t('BSPortal_Common_Cancel')}
				>
					{renderIcon('exit')}
				</Link>
			</LoadingOverlayWrapper>
		);
	}
}

function mapStateToProps(state: IStore, ownProps: IOwnProps) {
	const manageProduct: IManageProduct = state.manageProduct;
	let currentFormValues: IProductForm = null;
	const initialValues = createInitialValues(
		manageProduct.editProducts,
		ownProps.params.variantParentId,
		state.domainValues.categories,
	);
	if (state.form[c.FORM_NAME_EDIT_PRODUCT] && state.form[c.FORM_NAME_EDIT_PRODUCT].values) {
		currentFormValues = state.form[c.FORM_NAME_EDIT_PRODUCT].values;
	} else {
		currentFormValues = initialValues;
	}

	return {
		manageProduct,
		initialValues: initialValues as Partial<IProductForm>,
		currentFormValues,
		formErrors: getFormSyncErrors(c.FORM_NAME_EDIT_PRODUCT)(state),
		userPreferences: state.userPreferences,
	};
}

let EditProduct = reduxForm(
	{
		form: c.FORM_NAME_EDIT_PRODUCT,
		enableReinitialize: true,
	},
	mapStateToProps,
)(EditProductForm);

const mapDispatchToProps = (dispatch: Dispatch<IStore>, ownProps: IOwnProps) => ({
	getOptionsData: () => {
		// get categories and brands
		dispatch(domainValuesMap.categories());
		dispatch(domainValuesMap.brands());

		// get domain values for all possible attributes
		for (const attr of Object.values(attributesDefinitions)) {
			if (attr.domainValue) {
				dispatch(domainValuesMap[attr.domainValue]());
			}
		}
	},
	submitForm: (product: IProductForm) =>
		dispatch(submitForm(product, ownProps.params.id, ownProps.params.variantParentId ? 'edit' : 'add')),
	loadData: () => dispatch(getProductVariants(ownProps.params.variantParentId)),
	clearFormData: () => dispatch(clearFormData()),
	changeFormField: (form: string, field: string, data: any) => dispatch(change(form, field, data)),
});

EditProduct = connect(mapStateToProps, mapDispatchToProps)(EditProduct);

export { EditProduct };
